import {
  createContext,
  createEffect,
  createMemo,
  onCleanup,
  type ParentComponent,
  untrack,
  useContext,
} from "solid-js";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { createPowerSyncQuery } from "@bso/shared/solid";
import { useUserPreferences } from "@/providers/user-preferences-context";
import { useSession } from "@/lib/auth";
import {
  type CachedFont,
  getFontCatalogPresenceQuery,
  getFontFamilyQuery,
  getFontFamilyVariantsQuery,
  getFontKey,
  mapFontFamilyWithVariants,
  queryKeys,
  readCachedFont,
  resolveAvailableFontId,
  resolveDesiredFontId,
  resolveFontId,
  typographyMachine,
  updateUserFontFamilyMutation,
  VISITOR_ID,
  writeCachedFont,
} from "@bso/shared";
import { createActor } from "xstate";
import { fromActorRef } from "@fleet/state-machines/solid";
import { FontDownloaderService } from "@/utils/font-downloader";
import { BRAND_FONT } from "@/config/brand-font";

interface TypographyContextValue {
  /** The active font_family DB id, or null when the app is on its brand default. */
  fontFamily: () => string | null;
  /** The resolved display family name, or null when on the brand default. */
  fontFamilyName: () => string | null;
  /**
   * The EFFECTIVE font id for the active account (drives the selected state in the
   * UI). `null` = the app's brand default — which is also what an unavailable font
   * resolves to, so the default shows as selected when the chosen font is gone.
   */
  selectedFontId: () => string | null;
  /** True when the account's chosen font is no longer available (removed from catalog).*/
  isSelectedUnavailable: () => boolean;
  /** Display name of the chosen-but-unavailable font (for the UI notice), else null. */
  unavailableFontName: () => string | null;
  loadedRev: () => number;
  isTypoLoading: () => boolean;
  /** Human label of the app's bundled brand font (the reset target). */
  brandLabel: string;
  /** Apply + persist a catalog font for the active account (live, per-account). */
  setFont: (font: CachedFont) => Promise<void>;
  /** Reset to the app's bundled brand font (clears any override; catalog-independent). */
  resetFont: () => Promise<void>;
}

const TypographyContext = createContext<TypographyContextValue>();

/** Best-effort access to localStorage (unavailable in some private-mode webviews). */
function getStorage(): Storage | null {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

/**
 * Applies (or clears) the runtime font override on <html>. Clearing it falls the
 * `--font-*` role tokens straight back to the bundled brand font (see global.css)
 * — the reset path with zero dependency on the catalog.
 */
export function applyFontOverride(name: string | null): void {
  const root = document.documentElement;
  if (name) {
    // Keep a generic fallback chain so that if the chosen face fails to load
    // (offline, CDN hiccup) text stays readable instead of vanishing.
    root.style.setProperty("--font-user", `'${name}', ui-sans-serif, system-ui, sans-serif`);
  } else {
    root.style.removeProperty("--font-user");
  }
}

export const TypographyProvider: ParentComponent = (props) => {
  const { db: psDb } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { preferences, isLoading: isPreferencesLoading } = useUserPreferences();

  // Per-account "pending" local choice: held until that account's synced value
  // catches up, so the async DB round-trip can't revert a fresh pick. Keyed by
  // userId (the guest has its own entry). See resolveDesiredFontId.
  const pending = new Map<string, string | null>();

  // ── Effective per-account font id (brand default = null) ────────────────────
  // Mirrors the per-account language pattern: guest ignores synced prefs, real
  // account trusts its synced value once loaded, cache is the instant-boot floor.
  const desiredFontId = createMemo<string | null>(() => {
    const userId = session.userId();
    const isGuest = userId === VISITOR_ID;
    const cached = readCachedFont(getStorage(), userId);
    const { fontId, clearPending } = resolveDesiredFontId({
      isGuest,
      preferencesLoaded: !isPreferencesLoading(),
      remoteFontId: isGuest ? null : (preferences()?.font_family ?? null),
      cachedFontId: cached?.id ?? null,
      pendingFontId: pending.has(userId) ? (pending.get(userId) ?? null) : undefined,
    });
    if (clearPending) {
      pending.delete(userId);
    }
    return fontId;
  });

  // Query the chosen family + its variants from the (optional) synced catalog.
  const familyData = createPowerSyncQuery({
    queryKey: () => queryKeys.fonts.family(desiredFontId()),
    db: psDb,
    queryFn: () => {
      const db = drizzle();
      const fid = desiredFontId();
      if (!db || !fid) {
        return null;
      }
      return getFontFamilyQuery(db, fid);
    },
    enabled: () => Boolean(desiredFontId()) && Boolean(drizzle()),
    suspense: false,
  });

  // Presence probe: is the synced catalog available locally at all? Lets us tell
  // "catalog not synced yet" (empty) from "the chosen font was removed" (catalog
  // present but the family is absent/inactive) — see resolveAvailableFontId.
  const catalogPresence = createPowerSyncQuery({
    queryKey: () => queryKeys.fonts.catalogPresence(),
    db: psDb,
    queryFn: () => {
      const db = drizzle();
      return db ? getFontCatalogPresenceQuery(db) : null;
    },
    enabled: () => Boolean(drizzle()),
    suspense: false,
  });

  const catalogLoaded = () => !catalogPresence.isLoading && (catalogPresence.data?.length ?? 0) > 0;
  const familyFound = () => !familyData.isLoading && (familyData.data?.length ?? 0) > 0;

  // Graceful availability: a font removed/deactivated from the catalog degrades to
  // the bundled BRAND default (null) instead of a broken generic face. Until the
  // catalog is confirmed present we keep the desired choice (instant-boot).
  const availability = createMemo(() =>
    resolveAvailableFontId({
      desiredFontId: desiredFontId(),
      familyFound: familyFound(),
      catalogLoaded: catalogLoaded(),
    }),
  );
  /** Effective font id to apply (brand `null` when the desired one is unavailable). */
  const effectiveFontId = () => availability().fontId;

  const resolvedId = createMemo(() => resolveFontId(familyData.data ?? [], desiredFontId()));

  const variantsData = createPowerSyncQuery({
    queryKey: () => queryKeys.fonts.familyVariants(desiredFontId()),
    db: psDb,
    queryFn: () => {
      const db = drizzle();
      const rid = resolvedId();
      if (!db || !rid) {
        return null;
      }
      return getFontFamilyVariantsQuery(db, rid);
    },
    enabled: () => Boolean(resolvedId()) && Boolean(drizzle()),
    suspense: false,
  });

  const fontData = createMemo(() => {
    if (!familyData.data || !variantsData.data) {
      return null;
    }
    return mapFontFamilyWithVariants(familyData.data, variantsData.data);
  });

  const actor = createActor(typographyMachine, {
    input: {
      downloader: FontDownloaderService,
      getFontKey,
    },
  });
  actor.start();
  onCleanup(() => {
    actor.stop();
  });

  const state = fromActorRef(actor);

  // Sync data to the machine when queries resolve.
  createEffect(() => {
    const ps = psDb();
    if (!ps) {
      return;
    }
    actor.send({
      type: "SYNC_DATA",
      // Effective id: a removed/unavailable font resolves to brand (null) so the
      // machine never tries to download a dead face.
      fontFamilyId: effectiveFontId(),
      fontData: fontData(),
      db: ps,
    });
  });

  // ── Apply the effective font (instant, catalog-independent) ─────────────────
  // The name to apply, resolved with an instant-boot cache fallback so a
  // returning user sees their font immediately (before the catalog/download
  // resolves), and brand (null) applies with zero catalog dependency.
  const effectiveName = createMemo<string | null>(() => {
    const id = effectiveFontId();
    if (!id) {
      return null; // brand default (or unavailable → fell back) — clears the override
    }
    // Authoritative once the machine has downloaded & settled the chosen family.
    const machineName =
      state().context.activeFamilyId === id ? state().context.activeFamilyName : null;
    if (machineName) {
      return machineName;
    }
    // Instant-boot fallback: the per-account cache carries the display name.
    const cached = readCachedFont(getStorage(), session.userId());
    return cached && cached.id === id ? cached.name : null;
  });

  createEffect(() => {
    applyFontOverride(effectiveName());
  });

  // Mirror the settled choice into the per-account cache (instant boot next time,
  // and keeps a real account's cache in sync with its synced value). Never clobber
  // a still-pending pick.
  createEffect(() => {
    if (!state().matches("ready")) {
      return;
    }
    const id = state().context.activeFamilyId;
    const name = state().context.activeFamilyName;
    const userId = untrack(() => session.userId());
    if (pending.has(userId)) {
      return;
    }
    writeCachedFont(getStorage(), userId, id && name ? { id, name } : null);
  });

  const isTypoLoading = () =>
    isPreferencesLoading() ||
    familyData.isLoading ||
    variantsData.isLoading ||
    state().matches("downloading") ||
    state().matches("evaluating");

  // ── Public actions (per-account, local-first) ───────────────────────────────
  const setFont = async (font: CachedFont): Promise<void> => {
    const userId = session.userId();
    pending.set(userId, font.id);
    // Persist to the per-account cache immediately (instant boot, offline, guest).
    writeCachedFont(getStorage(), userId, font);
    // Apply live right away — do not wait for the DB round-trip / download.
    applyFontOverride(font.name);
    const db = drizzle();
    if (db && userId !== VISITOR_ID) {
      await updateUserFontFamilyMutation(db, userId, font.id);
    }
  };

  const resetFont = async (): Promise<void> => {
    const userId = session.userId();
    pending.set(userId, null);
    // Clear the per-account cache and the override → the bundled brand font.
    writeCachedFont(getStorage(), userId, null);
    applyFontOverride(null);
    const db = drizzle();
    if (db && userId !== VISITOR_ID) {
      await updateUserFontFamilyMutation(db, userId, null);
    }
  };

  return (
    <TypographyContext.Provider
      value={{
        fontFamily: () => state().context.activeFamilyId,
        fontFamilyName: () => state().context.activeFamilyName,
        selectedFontId: effectiveFontId,
        isSelectedUnavailable: () => availability().unavailable,
        unavailableFontName: () => {
          if (!availability().unavailable) {
            return null;
          }
          const desired = desiredFontId();
          const cached = readCachedFont(getStorage(), session.userId());
          return cached && cached.id === desired ? cached.name : desired;
        },
        loadedRev: () => state().context.loadedRev,
        isTypoLoading,
        brandLabel: BRAND_FONT.label,
        setFont,
        resetFont,
      }}
    >
      {props.children}
    </TypographyContext.Provider>
  );
};

export const useTypography = () => {
  const ctx = useContext(TypographyContext);
  if (!ctx) {
    throw new Error("useTypography must be used within TypographyProvider");
  }
  return ctx;
};
